import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { LOCATIONS } from "@/data/content";
import { SectionHead } from "@/components/Blocks";
import { Reveal, EASE } from "@/components/motion";

export default function LocationSwitcher({ withHead = true }) {
  const [active, setActive] = useState(0);
  const loc = LOCATIONS[active];
  return (
    <section data-testid="locations-section" className="bg-[#070A12] py-24 sm:py-36">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        {withHead && (
          <SectionHead
            eyebrow="Global bespoke delivery"
            lines={["Wherever you are.", "However you need it."]}
            intro="Recovery delivered on your terms — online worldwide, at No. 2 Harley Street, at our Andalusia mountain retreat, or at your private residence."
          />
        )}
        <Reveal className={`${withHead ? "mt-16" : ""} flex flex-wrap gap-2 border-b border-white/10`}>
          {LOCATIONS.map((l, i) => (
            <button
              key={l.id}
              onClick={() => setActive(i)}
              data-testid={`location-tab-${l.id}`}
              className={`relative px-4 sm:px-6 py-4 text-sm tracking-wide transition-colors duration-300 ${active === i ? "text-[#E2C68C]" : "text-slate-400 hover:text-slate-100"}`}
            >
              {l.tab}
              {active === i && <motion.span layoutId="loc-underline" className="absolute left-0 right-0 -bottom-px h-px bg-[#C5A059]" />}
            </button>
          ))}
        </Reveal>

        <div className="mt-10 grid lg:grid-cols-12 gap-8 lg:gap-14">
          <div className="lg:col-span-7 relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden bg-[#131B2E]">
            <AnimatePresence initial={false}>
              <motion.img
                key={loc.id}
                src={loc.image}
                alt={loc.title}
                data-testid="location-image"
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ clipPath: "inset(0 100% 0 0)", scale: 1.1 }}
                animate={{ clipPath: "inset(0 0% 0 0)", scale: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.8, delay: 0.4 } }}
                transition={{ duration: 1.2, ease: EASE }}
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-[#070A12]/70 to-transparent pointer-events-none" />
            <span className="absolute left-5 bottom-5 font-data text-[11px] tracking-[0.25em] uppercase text-slate-100 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#C5A059]" /> {loc.region}
            </span>
          </div>
          <div className="lg:col-span-5 grid">
            <AnimatePresence initial={false}>
              <motion.div
                key={loc.id}
                className="[grid-area:1/1] bg-[#131B2E] border border-[#C5A059]/20 p-8 sm:p-10 flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE, delay: 0.15 } }}
                exit={{ opacity: 0, transition: { duration: 0.3 } }}
                data-testid="location-details"
              >
                <p className="font-data text-[11px] tracking-[0.25em] uppercase text-slate-400">0{active + 1} / 0{LOCATIONS.length}</p>
                <h3 className="font-display text-4xl sm:text-5xl text-slate-50 mt-4">{loc.title}</h3>
                <p className="text-[#E2C68C] text-sm mt-2">{loc.region}</p>
                <p className="mt-6 text-slate-300 leading-relaxed text-sm sm:text-base">{loc.text}</p>
                <ul className="mt-8 space-y-3 border-t border-white/10 pt-6">
                  {loc.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm text-slate-200">
                      <span className="w-2 h-2 rotate-45 border border-[#C5A059]" /> {p}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
